/**
 * Utilities for managing sequential care plan flows
 */

import { FollowupInfo, Imaging, Lab, Prescription, Referral } from "@/types/interfaces/care"

export interface SequentialFlowParams {
    isSequential: boolean
    careId: string | null
}

/**
 * Extract sequential flow parameters from URL search params
 */
export const getSequentialParams = (searchParams: URLSearchParams): SequentialFlowParams => {
    return {
        isSequential: searchParams.get('sequential') === 'true',
        careId: searchParams.get('careId')
    }
}

/**
 * Build URL for sequential next steps page
 */
export const buildNextStepsUrl = (careId: string): string => {
    return `/care-plan/${careId}/next-steps`
}

/**
 * Add sequential parameters to a URL if they should be preserved
 */
export const addSequentialParams = (baseUrl: string, searchParams: URLSearchParams): string => {
    const { isSequential, careId } = getSequentialParams(searchParams)
    
    if (isSequential && careId) {
        const separator = baseUrl.includes('?') ? '&' : '?'
        return `${baseUrl}${separator}sequential=true&careId=${careId}`
    }
    
    return baseUrl
}

/**
 * Build a URL with sequential parameters for care item flows
 */
export const buildSequentialCareUrl = (baseUrl: string, careId: string): string => {
    const separator = baseUrl.includes('?') ? '&' : '?'
    return `${baseUrl}${separator}sequential=true&careId=${careId}`
}

/**
 * Check if current request is part of a sequential flow and get redirect URL
 */
export const getSequentialRedirectUrl = (searchParams: URLSearchParams): string | null => {
    const { isSequential, careId } = getSequentialParams(searchParams)
    
    if (isSequential && careId) {
        return buildNextStepsUrl(careId)
    }
    
    return null
}

/**
 * Completion state checkers for different care item types
 */
export const isCompleted = {
    prescription: (item: Prescription): boolean => {
        return item.state === 'booked' || item.state === 'confirmed' || item.state === 'completed' || item.state === 'awaiting_submission'
    },
    
    imaging: (item: Imaging): boolean => {
        return item.state === 'booked' || item.state === 'confirmed' || item.state === 'completed'
    },
    
    lab: (item: Lab): boolean => {
        return item.state === 'booked' || item.state === 'confirmed' || item.state === 'completed'
    },
    
    referral: (item: Referral): boolean => {
        return item.state === 'booked' || item.state === 'confirmed' || item.state === 'completed'
    },
    
    followUp: (followupInfo: FollowupInfo): boolean => {
        return followupInfo.state === 'booked' || followupInfo.state === 'confirmed' || followupInfo.state === 'completed'
    }
}